import * as api from './deliveryTermsApi';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import type { DeliveryTerm, Language } from './deliveryTermsApi';
import { DetailViewShell, FormRow, mergeLocalizations, sx13 } from '../common/detailView';
import { useCallback, useEffect, useState } from 'react';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    deliveryTermId: number | null;
}

export function DeliveryTermView(props: Props) {
    return (
        <ModalProvider>
            <DeliveryTermViewInner {...props} />
        </ModalProvider>
    );
}

function DeliveryTermViewInner({ deliveryTermId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<DeliveryTerm>();
    const [languages, setLanguages] = useState<Language[]>([]);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const langs = await api.getLanguages();
            setLanguages(langs);
            if (deliveryTermId === null) {
                detail.setItem({ id: 0, code: '', name: '', localizations: mergeLocalizations([], langs, buildDefaultLoc) });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getDeliveryTerm(deliveryTermId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [deliveryTermId]);

    useEffect(() => { load(); }, [load]);

    const isCodeValid = (): boolean => !!detail.item?.code && detail.item.code.length <= 20;

    const save = async () => {
        await detail.withSaving(async () => {
            const item = detail.item!;
            const response = detail.isNew ? await api.createDeliveryTerm(item) : await api.updateDeliveryTerm(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('deliveryterm.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        await detail.withSaving(async () => {
            await api.deleteDeliveryTerm(detail.item!.id);
            window.history.back();
        });
    };

    const updateLocalization = (languageId: number, name: string) => {
        if (!detail.item) return;
        detail.update('localizations', detail.item.localizations.map((loc) => (loc.language.id === languageId ? { ...loc, name } : loc)
        ));
    };

    return (
        <DetailViewShell<DeliveryTerm>
            featureKey="devFeature_reactDeliveryTermView"
            title={detail.item ? `${detail.item.code} ${detail.item.name}` : ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const sortedLocs = [...item.localizations].sort((a, b) => (a.language.name ?? '').localeCompare(b.language.name ?? '')
                );
                return (
                    <Stack spacing={2}>
                        <FormRow label={t('deliveryterm.code')}>
                            {!detail.isNew && !detail.isEditing ? (
                                <Typography sx={sx13}>{item.code}</Typography>
                            ) : (
                                <TextField size="small" fullWidth
                                    inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                    value={item.code}
                                    onChange={(e) => detail.update('code', e.target.value.toUpperCase())}
                                    error={!isCodeValid()} />
                            )}
                        </FormRow>

                        <FormRow label={t('deliveryterm.name')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{item.name}</Typography>
                            ) : (
                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                    value={item.name} onChange={(e) => detail.update('name', e.target.value)} />
                            )}
                        </FormRow>

                        <FormRow label={t('deliveryterm.leadTimeInDays')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{item.leadTimeInDays}</Typography>
                            ) : (
                                <TextField size="small" type="number" fullWidth
                                    value={item.leadTimeInDays ?? ''}
                                    onChange={(e) => detail.update('leadTimeInDays', e.target.value === '' ? undefined : Number(e.target.value))} />
                            )}
                        </FormRow>

                        <Box />

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('deliveryterm.name')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLocs.map((loc) => (
                                    <TableRow key={loc.language.id}>
                                        <TableCell sx={{ ...sx13, width: 150 }}>{loc.language.name}</TableCell>
                                        <TableCell>
                                            {!detail.isEditing ? (
                                                <Typography sx={sx13}>{loc.name}</Typography>
                                            ) : (
                                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                    value={loc.name ?? ''}
                                                    onChange={(e) => updateLocalization(loc.language.id, e.target.value)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Stack>
                );
            }}
        </DetailViewShell>
    );
}
